import AlgoliaUtils from '../../algoliaUtils';
import AlgoliaRenderer from '../../renderer/algoliaRenderer';
import AlgoliaAjaxActions from '../../algoliaAjaxActions';

class AlgoliaEventListener {
    constructor() {
        this.class = {
            btnOpenOverlay: '.js-gs-btn-open-overlay',
            btnOpenOverlayMobile: '.js-gs-btn-open-overlay--mobile',
            btnCloseOverlay: '.js-gs-overlay-close',
            btnClearSearchInput: '.js-gs-overlay-clear',
            overlay: '.js-gs-overlay',
            searchForm: '.js-gs-overlay-searchform',
            searchInput: '.js-gs-overlay-searchinput',
            btnSubmitSearch: '.js-gs-overlay-submit',
            suggestions: '.js-gs-suggestions-no-input',
            suggestionsWithInput: '.js-gs-suggestions-input',
        };

        if (document.readyState === 'loading') {
            document.addEventListener('DOMContentLoaded', this.init.bind(this), false);
        } else {
            this.init();
        }
    }

    async init() {
        this.listenForInteraction();
    }

    listenForInteraction() {
        // basic elements
        const overlay = document.querySelector(this.class.overlay);
        const btnOpenOverlay = document.querySelector(this.class.btnOpenOverlay);
        const btnOpenOverlayMobile = document.querySelector(this.class.btnOpenOverlayMobile);
        const btnCloseOverlay = document.querySelector(this.class.btnCloseOverlay);
        const btnClearSearchInput = document.querySelector(this.class.btnClearSearchInput);

        // elements inside the overlay
        const searchForm = document.querySelector(this.class.searchForm);
        const searchInput = document.querySelector(this.class.searchInput);
        const btnSubmitSearch = document.querySelector(this.class.btnSubmitSearch);

        // reflects whether a submit would actually be allowed (see the 'submit' listener below)
        const updateSubmitState = () => {
            const currentPageOnlyChecked = searchForm.querySelector('.js-gs-searchform--filter-checkbox:checked');

            btnSubmitSearch.disabled = !AlgoliaUtils.hasRealQuery(searchInput.value) && !currentPageOnlyChecked;
        };

        updateSubmitState();
        searchForm.addEventListener('change', updateSubmitState);

        const staticQueries = document.querySelector('.js-gs-suggestions--static-queries');

        staticQueries.addEventListener('click', async (event) => {
            if (event.target.tagName === 'BUTTON') {
                const searchString = event.target.dataset.query;

                document.querySelector(this.class.searchInput).value = searchString;
                searchInput.dispatchEvent(new Event('input', { bubbles: true }));
            }
        });

        const topSearches = document.querySelector('.js-gs-suggestions--others-top-searches');
        topSearches.addEventListener('click', async (event) => {
            if (event.target.tagName === 'BUTTON') {
                const searchString = event.target.dataset.query;

                document.querySelector(this.class.searchInput).value = searchString;
                searchInput.dispatchEvent(new Event('input', { bubbles: true }));
            }
        });

        const bestQueries = document.querySelector('.js-gs-suggestions--best-queries');
        bestQueries.addEventListener('click', async (event) => {
            if (event.target.tagName === 'BUTTON') {
                const searchString = event.target.dataset.query;

                document.querySelector(this.class.searchInput).value = searchString;
                searchInput.dispatchEvent(new Event('input', { bubbles: true }));
            }
        });

        const bestResults = document.querySelector('.js-gs-suggestions--best-results');
        bestResults.addEventListener('click', async (event) => {
            if (event.target.tagName === 'A') {
                const searchString = event.target.textContent;

                document.querySelector(this.class.searchInput).value = searchString;
                searchInput.dispatchEvent(new Event('input', { bubbles: true }));
            }
        });

        btnClearSearchInput.addEventListener('click', () => {
            AlgoliaUtils.updateLocationParams('', []);
            AlgoliaRenderer.toggleSuggestions('initial');
        });

        // watch for user input in the search input field
        searchInput.addEventListener('input', async (Event) => {
            const searchString = Event.target.value.toLowerCase().trim();

            updateSubmitState();

            const searchFacets = [];
            const searchCurrentPageOnly = searchForm.querySelector('.js-gs-searchform--filter-checkbox:checked');
            const searchCurrentPage = searchCurrentPageOnly ? searchCurrentPageOnly.value : null;

            if (searchCurrentPage !== null) {
                searchFacets.push(`'package:${searchCurrentPage}'`);
            }

            const hasRealQuery = AlgoliaUtils.hasRealQuery(searchString);

            if (Event.target.value.length <= 2 || !hasRealQuery) {
                AlgoliaRenderer.toggleSuggestions('initial');
            }

            if (Event.target.value.length > 2 && hasRealQuery) {
                AlgoliaRenderer.toggleSuggestions('performed');
                // avoid flashing a stale (e.g. no-results) message while the new request is in flight
                AlgoliaRenderer.clearAutosuggestResults();

                // example: call PHP AJAX endpoint instead of direct Algolia JS client
                const quickResults = await AlgoliaAjaxActions.performAjaxQuickResults();

                // superseded by a newer keystroke while this request was in flight/aborted
                if (!quickResults) {
                    return;
                }

                const [suggestionsResult, ...topResults] = quickResults.results;

                AlgoliaRenderer.renderAutosuggestResults(searchString, suggestionsResult.hits);
                AlgoliaRenderer.renderTopResults(searchString, topResults);
            }
        });

        // handle overlay state
        // 'inert' keeps the (still off-screen) overlay content out of the tab order while closed
        const openOverlay = () => {
            overlay.setAttribute('open', 'open');
            overlay.removeAttribute('inert');
        };

        const closeOverlay = () => {
            overlay.setAttribute('inert', 'inert');
            overlay.removeAttribute('open');
        };

        // remembers which toggle button opened the overlay, so ESC can return focus to it
        let lastOverlayTrigger = null;

        btnOpenOverlay.addEventListener('click', () => {
            lastOverlayTrigger = btnOpenOverlay;
            openOverlay();
            AlgoliaUtils.checkForCurrentPageOnlyFilter();
            searchInput.focus();
        });

        btnOpenOverlayMobile.addEventListener('click', () => {
            lastOverlayTrigger = btnOpenOverlayMobile;
            openOverlay();
            searchInput.focus();
        });

        btnCloseOverlay.addEventListener('click', () => {
            closeOverlay();
        });

        // close overlay on ESC when no child element (e.g. input) holds the focus
        document.addEventListener('keydown', (event) => {
            if (event.key !== 'Escape') {
                return;
            }

            if (!overlay.hasAttribute('open')) {
                return;
            }

            if (overlay.contains(document.activeElement)) {
                document.activeElement.blur();
                return;
            }

            closeOverlay();

            if (lastOverlayTrigger) {
                lastOverlayTrigger.focus();
            }
        });

        // close overlay once focus moves to an element outside of it (e.g. via Tab)
        document.addEventListener('focusin', (event) => {
            if (!overlay.hasAttribute('open')) {
                return;
            }

            if (overlay.contains(event.target)) {
                return;
            }

            closeOverlay();
        });

        searchForm.addEventListener('reset', async () => {
            AlgoliaRenderer.toggleSuggestions('initial');
            // the 'reset' event fires before the browser actually clears the field values
            setTimeout(updateSubmitState, 0);
        });

        searchForm.addEventListener('submit', async (Event) => {
            // const searchString = Event.target.value.toLowerCase().trim();
            // const searchInputField = Event.target.querySelector('[type="search"]');
            // const searchString = searchInputField.value.toLowerCase().trim();
            const searchFacets = [];
            const searchCurrentPageOnly = searchForm.querySelector('.js-gs-searchform--filter-checkbox:checked');
            const searchCurrentPage = searchCurrentPageOnly ? searchCurrentPageOnly.value : null;

            if (searchCurrentPage !== null) {
                searchFacets.push(`'package:${searchCurrentPage}'`);
            }

            // block an empty/invalid query so it never reaches Algolia with a "match everything" request
            if (!AlgoliaUtils.hasRealQuery(searchInput.value) && searchCurrentPage === null) {
                Event.preventDefault();
                return;
            }

            // already on the search results page: update it in place via AJAX instead of a full reload;
            // any other page must fall through to the native navigation to the search results page
            if (AlgoliaUtils.isOnSearchResultsPage()) {
                Event.preventDefault();

                const ajaxResults = await AlgoliaAjaxActions.performAjaxSearch();

                btnCloseOverlay.click();

                AlgoliaRenderer.renderSearchResultsFacets(ajaxResults);
                AlgoliaRenderer.renderResultsCount(ajaxResults);
                AlgoliaRenderer.renderSearchResults(ajaxResults);
                AlgoliaRenderer.renderPagination(ajaxResults);
                AlgoliaRenderer.renderPageCount(ajaxResults);
            }
        });
    }
}

export default new AlgoliaEventListener();
